import React, { useMemo } from 'react';
import { Container, Row, Col, Card, Button } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell
} from 'recharts';

const AnalyticsPage = ({ transactions }) => {

  // --- 1. Lógica de Procesamiento de Datos ---

  const { monthlyData, expenseCategoryData, forecastData } = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const next30Days = new Date(today);
    next30Days.setDate(today.getDate() + 30);

    const monthsMap = {};
    const categoryMap = {};
    let forecastIncome = 0;
    let forecastExpense = 0;
    let forecastCount = 0;

    transactions.forEach(t => {
      // Cálculo de montos netos (Igual que en Summary.js)
      let netAmount = 0;
      if (t.type === 'ingreso') {
        netAmount = t.amount + (t.dap_interest || 0) - (t.cc_commission || 0) - (t.penalizaciones_booking || 0);
      } else {
        netAmount = t.amount - (t.cc_commission || 0);
      }

      // --- Datos Históricos (Solo Completados) ---
      if (t.status === 'completado') {
        const monthKey = t.date.substring(0, 7); // YYYY-MM

        if (!monthsMap[monthKey]) {
          monthsMap[monthKey] = { name: monthKey, ingresos: 0, egresos: 0, neto: 0 };
        }

        if (t.type === 'ingreso') {
          monthsMap[monthKey].ingresos += netAmount;
        } else {
          monthsMap[monthKey].egresos += netAmount;
          
          // Categorías de Gastos
          if (!categoryMap[t.category]) {
            categoryMap[t.category] = 0;
          }
          categoryMap[t.category] += netAmount;
        }
        monthsMap[monthKey].neto = monthsMap[monthKey].ingresos - monthsMap[monthKey].egresos;
      }

      // --- Datos de Proyección (Pendientes, Por Procesar y Esperando OC/HES en rango 30 días) ---
      const tDate = new Date(t.date + 'T00:00:00'); // Asegurar interpretación local correcta
      const isPending = t.status === 'pendiente' || 
                        (t.status && t.status.toLowerCase() === 'por procesar') || 
                        (t.status && t.status.toLowerCase() === 'esperando oc/hes');
      
      if (isPending && tDate >= today && tDate <= next30Days) {
        // Para proyección usamos el monto bruto ya que intereses/comisiones pueden no estar definidos aún
        // o usamos la misma lógica si ya existen los datos.
        const projectedAmount = t.amount; 
        
        if (t.type === 'ingreso') {
          forecastIncome += projectedAmount;
        } else {
          forecastExpense += projectedAmount;
        }
        forecastCount++;
      }
    });

    // Formatear para Recharts
    const sortedMonths = Object.values(monthsMap).sort((a, b) => a.name.localeCompare(b.name));
    
    const sortedCategories = Object.entries(categoryMap)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 6); // Top 6 categorías

    return {
      monthlyData: sortedMonths,
      expenseCategoryData: sortedCategories,
      forecastData: {
        income: forecastIncome,
        expense: forecastExpense,
        net: forecastIncome - forecastExpense,
        count: forecastCount
      }
    };
  }, [transactions]);

  // --- Formateadores ---
  const formatCurrency = (val) => new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 }).format(val);
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d'];

  return (
    <Container className="py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Analítica Financiera</h2>
        <Link to="/">
          <Button variant="outline-secondary">Volver al Gestor</Button>
        </Link>
      </div>

      {/* --- Sección 1: Proyección (Forecast) --- */}
      <Row className="mb-4">
        <Col xs={12}>
          <Card className="border-info shadow-sm">
            <Card.Header className="bg-info text-white fw-bold">
              🔮 Proyección a 30 Días (Forecast)
            </Card.Header>
            <Card.Body>
              <Row className="text-center">
                <Col md={3}>
                  <h6 className="text-muted">Transacciones Próximas</h6>
                  <h3>{forecastData.count}</h3>
                </Col>
                <Col md={3}>
                  <h6 className="text-success">Ingresos Proyectados</h6>
                  <h3>{formatCurrency(forecastData.income)}</h3>
                </Col>
                <Col md={3}>
                  <h6 className="text-danger">Egresos Proyectados</h6>
                  <h3>{formatCurrency(forecastData.expense)}</h3>
                </Col>
                <Col md={3}>
                  <h6 className="text-primary">Flujo Neto Esperado</h6>
                  <h3>{formatCurrency(forecastData.net)}</h3>
                </Col>
              </Row>
              <div className="text-muted mt-2 small text-center">
                * Calculado en base a transacciones 'Pendientes', 'Por Procesar' y 'Esperando OC/HES' que vencen en los próximos 30 días.
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* --- Sección 2: Gráficos --- */}
      <Row className="g-4">
        {/* Gráfico de Evolución Mensual */}
        <Col lg={8}>
          <Card className="h-100 shadow-sm">
            <Card.Body>
              <Card.Title>Evolución de Flujo de Caja (Completado)</Card.Title>
              <div style={{ height: 350, width: '100%' }}>
                <ResponsiveContainer>
                  <BarChart data={monthlyData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis tickFormatter={(val) => `$${val/1000}k`} />
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                    <Legend />
                    <Bar dataKey="ingresos" name="Ingresos" fill="#198754" />
                    <Bar dataKey="egresos" name="Egresos" fill="#dc3545" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* Gráfico de Distribución de Gastos */}
        <Col lg={4}>
          <Card className="h-100 shadow-sm">
            <Card.Body>
              <Card.Title>Top Gastos por Categoría</Card.Title>
              <div style={{ height: 350, width: '100%' }}>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie
                      data={expenseCategoryData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="value"
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    >
                      {expenseCategoryData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default AnalyticsPage;
